import { cloneDeep, partition, uniq, uniqWith } from 'lodash';
import isEqual from 'lodash/isEqual';

import { FIELD_TYPE_SELECT } from 'SharedDir/constants/field-types';
import { IssueStepStatus } from 'SharedDir/constants/issue-step-status';
import { RoutingType } from 'SharedDir/constants/route';
import { FieldValueSet } from 'SharedDir/declarations/fields';
import { NIssue } from 'SharedDir/declarations/issue';
import { Section, SectionsList } from 'SharedDir/declarations/section-field';
import { NotNull } from 'SharedDir/declarations/shared';
import SuperStep from 'SharedDir/service/issues/SuperStep';
import { RouteError } from 'SharedDir/service/issues/issue.exceptions';

import StepTransitions = NIssue.StepTransitions;
import StepSiblings = NIssue.StepSiblings;

/**
 * Достаем первый этап
 *
 * @throws {RouteError}
 * @param steps
 * @returns {NIssue.Step}
 */
export const getFirstStep = (steps: NIssue.FixedRoute['steps']): NIssue.Step => {
  const first = steps.filter((step) => step.isStart);
  if (first.length === 0) {
    throw new RouteError('First step not found');
  }
  if (first.length > 1) {
    throw new RouteError(
      `There are several first steps - ${first.map(({ name }) => `"${name}"`).join(', ')}`
    );
  }
  return first[0];
};

/**
 * Находим последний этап
 *
 * @throws {RouteError}
 * @param steps
 * @returns {NIssue.Step}
 */
export const getLastStep = (steps: NIssue.FixedRoute['steps']): NIssue.Step => {
  const last = steps.filter((step) => step.isFinal);
  if (last.length === 0) {
    throw new RouteError('Last step not found');
  }
  if (last.length > 1) {
    throw new RouteError(
      `There are several last steps - ${last.map(({ name }) => `"${name}"`).join(', ')}`
    );
  }
  return last[0];
};

/**
 * Находим этапы-отказы
 *
 * @throws {RouteError}
 * @param steps
 * @returns {Array<NIssue.Step>}
 */
export const getDeclineSteps = (steps: NIssue.FixedRoute['steps']): Array<NIssue.Step> =>
  steps.filter((step) => step.isDecline);

/**
 * Ищем переходы из шага
 *
 * @param step
 * @param transitions
 */
export const getStepTransitions = (
  step: NIssue.Step,
  transitions: NIssue.FixedRoute['transitions']
): StepTransitions & StepSiblings => {
  const incomingTransitions = transitions.filter((transition) => transition.toStepId === step.id);
  const parents = incomingTransitions.map(({ fromStepId }) => fromStepId);
  return {
    outgoingTransitions: transitions.filter((transition) => transition.fromStepId === step.id),
    incomingTransitions,
    siblings: transitions.filter((t) => parents.includes(t.fromStepId) && t.isParallel),
  };
};

/**
 * Проверяем параллельные ли переданные шаги
 *
 * А параллельные они тогда когда среди них есть хоть один параллельный переход
 *
 * @param transitions
 * @returns {boolean}
 */
export const checkIfParallel = (transitions: Array<NIssue.FixedTransition>): boolean =>
  !!transitions.find((transition) => transition.isParallel);

/**
 * Проверяем все ли паралельне переходы имеют общий этап
 *
 * @throws {RouteError}
 * @param transitions
 * @returns {void}
 */
export const checkCommonParent = (transitions: Array<NIssue.FixedTransition>): void => {
  const parentIds = transitions.map(({ fromStepId }) => fromStepId);
  if (uniq(parentIds).length > 1) {
    throw new RouteError('Trying to get parallel branches for not siblings');
  }
};

/**
 * Ищем этапы, которые идут параллельно
 * по параллельным переходам из родительского этапа
 * и переходы из них
 *
 * @param transitions
 * @param route
 * @returns{}
 */
export const getStepsBranches = (
  transitions: Array<NIssue.FixedTransition>,
  route: NIssue.FixedRoute
): Array<NIssue.StepWithMap> => {
  // проверяем на всякий случай не попали ли сюда левые переходы
  checkCommonParent(transitions);

  const stepIds = transitions.map(({ toStepId }) => toStepId);
  const relatedSteps = route.steps.filter(({ id }) => stepIds.includes(id));
  return relatedSteps.map((step) => ({
    ...step,
    ...getStepTransitions(step, route.transitions),
  }));
};

/**
 * Собираем сам этап и его параллельно-идущих детей в
 * абстрактный объект с которым удобно работать в интерфейсе
 *
 * @param step
 * @param stepTransitions
 * @param route
 * @returns {NIssue.StepSuperposition}
 */
export const makeComplexSuperStep = (
  step: NIssue.Step,
  stepTransitions: NIssue.StepTransitions & NIssue.StepSiblings,
  route: NIssue.FixedRoute
): NIssue.StepSuperposition => {
  const isParallel = (transition: NIssue.FixedTransition) => transition.isParallel;
  const [parallelTransitions] = partition(stepTransitions.outgoingTransitions, isParallel);
  const head = { ...step, ...stepTransitions };
  const branches = getStepsBranches(parallelTransitions, route);
  return new SuperStep(head, branches);
};

// noinspection GrazieInspection
/**
 * Преобразуем нативный шаг в абстракцию реализующую
 * интерфейс NIssue.StepSuperposition
 *
 * @throws {RouteError} - если у не конечного шага нет переходов
 * @param step
 * @param route
 * @returns {NIssue.StepSuperposition}
 */
export const makeStepSuperposition = (
  step: NIssue.Step,
  route: NIssue.FixedRoute
): NIssue.StepSuperposition => {
  const stepTransitions = getStepTransitions(step, route.transitions);
  console.log(stepTransitions, step, route.transitions);
  const outgoing = stepTransitions.outgoingTransitions;
  if (outgoing.length === 0 && !step.isFinal && !step.isDecline) {
    throw new RouteError(`There are no transitions for non-ending step "${step.name}"`);
  }

  console.debug('Step transitions', stepTransitions);

  if (checkIfParallel(outgoing)) {
    return makeComplexSuperStep(step, stepTransitions, route);
  }

  return new SuperStep({ ...step, ...stepTransitions });
};

/**
 * Проходимся по переходам и помечаем те что ведут в этапы-отмены
 *
 * @param route
 * @returns
 */
export const fixRoute = (route: NIssue.Route): NIssue.FixedRoute => {
  const fixed = cloneDeep(route);
  const declines = getDeclineSteps(fixed.steps).map(({ id }) => id);
  const fixedTransitions = fixed.transitions.map((transition) => ({
    ...transition,
    toDeclineStep: declines.includes(transition.toStepId),
  }));
  return {
    ...fixed,
    transitions: fixedTransitions,
  };
};

// noinspection GrazieInspection
/**
 * Распутываем нативные этапы и переходы в удобоваримые абстракции
 * реализующие интерфейс NIssue.StepSuperposition
 * *
 * Мы конечно доверяем бекэнду, но на всякий случаем защитимся от зацикливания
 * (в виде ограничения числа раундов поиска следующего шага),
 * поскольку у нас используется цикл с предусловием,
 * которое вычисляется на основе предидущего нативного шага, а чтичто технически нам могут тут
 * наделать бесконечных циклов при переходах между этапами.
 *
 * @param route {NIssue.FixedRoute}
 * @param maxTries - максимальное количество попыток найти следующий этап после которых
 * будет выброшена ошибка
 * @returns {Array<NIssue.StepSuperposition>}
 */
export const untangleBusinnessProccess = (
  route: NIssue.FixedRoute,
  maxTries: number = 100
): Array<NIssue.StepSuperposition> => {
  const firstStep = getFirstStep(route.steps);
  // проверяем наличие последнего этапа
  getLastStep(route.steps);
  const superSteps: Array<NIssue.StepSuperposition> = [];
  let superStep = makeStepSuperposition(firstStep, route);
  superSteps.push(superStep);
  let breaks = 0;
  while (!superStep.isFinal && breaks < maxTries) {
    const { nextStepId } = superStep;
    const nextStep = route.steps.find(({ id }) => id === nextStepId);
    if (!nextStep) {
      // Если ене находим следующий шаг в списке то что то не так,
      // потому что если бы это был послдений шаг то цикл бы остановился
      throw new RouteError('There is no next step for non-final step');
    }
    superStep = makeStepSuperposition(nextStep, route);
    superSteps.push(superStep);
    breaks += 1;
  }
  if (breaks === maxTries) {
    throw new RouteError(`Max tries(${maxTries} times) for seeking next steps were exceed`);
  }
  // поскольку при определении следующего шага учитываются только переходы 'approve'

  const declineSteps = getDeclineSteps(route.steps).map((step) =>
    makeStepSuperposition(step, route)
  );

  return [...superSteps, ...declineSteps];
};

type TransitionGraphItem = {
  isParallel: boolean;
  routingType: null | RoutingType;
  stepIds: Array<string>;
};
type TypeTransitionGraph = {
  [key: string]: TransitionGraphItem;
};

/**
 * Топологическая сортировка собранного графа
 * Необходимо передавать ацикличный граф
 *
 * IN: 0: ["21"]
      21: ["22", "23"]
      22: ["24"]
      23: ["24"]
      24: ["25"]
 *
 * OUT: ["25", "24", "22", "23", "21", "0"]
 * @param graph
 * @returns
 */
const topologicalSortTransitions = (graph: TypeTransitionGraph): Array<string> => {
  const result: Array<string> = [];
  const visited: { [key: string]: boolean } = {};
  const temp: { [key: string]: boolean } = {};

  const walkInGraph = (node) => {
    temp[node] = true;
    const nextSteps: Array<string> | undefined = graph[node]?.stepIds;
    if (typeof nextSteps === 'undefined') {
      temp[node] = false;
      visited[node] = true;
      result.push(node);
      return;
    }
    for (let i = 0; i < nextSteps.length; i += 1) {
      const n = nextSteps[i];
      if (temp[n]) {
        throw new Error('Граф цикличен');
      }
      if (!visited[n]) {
        walkInGraph(n);
      }
    }
    temp[node] = false;
    visited[node] = true;
    result.push(node);
  };

  Object.keys(graph).forEach((node) => {
    if (!visited[node] && !temp[node]) {
      walkInGraph(node);
    }
  });

  return result.reverse();
};

/**
 * Оставляем transitions только с прямыми переходами
 *
 * @param transitions
 * @returns
 */
export const clearFromFallbackTransitions = (
  transitions: Array<NIssue.Transition>
): Array<NIssue.Transition> => transitions.filter((t) => t.type === 'approve');

/**
 * Собираем объект с переходами
 *
 * @param transitions массив переходов
 * @param startStepId начальный этап
 * @param excludedStepsIds исключаемые этапы
 * @returns
 */
export const collectTransitionMapping = (
  transitions: Array<NIssue.Transition>,
  startStepId: number,
  excludedStepsIds: Array<number>
): TypeTransitionGraph => {
  const transitionMapping: TypeTransitionGraph = {};
  // Псевдопереход для первого этапа
  transitionMapping['0'] = {
    isParallel: false,
    routingType: null,
    stepIds: [startStepId.toString()],
  };

  transitions.forEach((t) => {
    // Исключаем этапы, в т.ч. отмены
    if (excludedStepsIds.includes(t.toStepId)) {
      return;
    }
    const fromStepId = t.fromStepId.toString();
    const toStepId = t.toStepId.toString();

    if (transitionMapping[fromStepId]) {
      transitionMapping[fromStepId].stepIds.push(toStepId);
      transitionMapping[fromStepId].isParallel = t.isParallel;
    } else {
      transitionMapping[fromStepId] = {
        isParallel: t.isParallel,
        routingType: t.routingType,
        stepIds: [toStepId],
      };
    }
  });

  return transitionMapping;
};

/**
 * Сортировка мэппинга
 *
 * @param transitionMapping
 * @param sortedTransitions
 * @returns
 */
export const sortTransitionMapping = (
  transitionMapping: TypeTransitionGraph,
  sortedTransitions: Array<string>
) => {
  const sortedTransitionMapping = [] as TransitionGraphItem[];
  sortedTransitions.forEach((val) => {
    if (transitionMapping[val]) {
      sortedTransitionMapping.push(transitionMapping[val]);
    }
  });
  return sortedTransitionMapping;
};

/**
 * Вычисляем тип Этапа
 *
 * @param objStep
 * @returns
 */
export const calculateStepGroupType = (objStep: NIssue.Step): NIssue.TransitionBoardType => {
  if (objStep.isStart) {
    return 'start';
  }
  if (objStep.isFinal) {
    return 'final';
  }
  if (objStep.isDecline) {
    return 'decline';
  }
  return 'step';
};

export function uniqueObjects(
  transitionMapping: TransitionGraphItem[]
): { isParallel: boolean; stepIds: Array<string>; routingType: RoutingType | null }[] {
  return uniqWith(transitionMapping, isEqual);
}

/**
 * Группировка шагов по отсортированным переходам
 *
 * @param transitionMapping
 * @param steps
 */
export const groupTransitionMappingToSteps = (
  transitionMapping: TransitionGraphItem[],
  steps: Array<NIssue.Step>
) => {
  const uniqueTransitionSteps = uniqueObjects(transitionMapping);

  function mapSteps({
    stepIds: transitionSteps,
    isParallel,
    routingType,
  }): NIssue.TransitionBoardItem {
    const objectSteps = transitionSteps
      .map((ts) => steps.find((s) => s.id.toString() === ts))
      .filter(Boolean) as Array<NIssue.Step>;

    return {
      steps: objectSteps,
      isParallel,
      routingType,
    };
  }

  const groupedSteps: NIssue.TransitionBoard = uniqueTransitionSteps.map(mapSteps);
  return groupedSteps;
};

/**
 * Ищем начальный шаг в списке шагов
 * @param steps
 */
export function findStartStep(steps: Array<NIssue.Step>) {
  return steps.find((step) => step.isStart) as NIssue.Step;
}

/**
 * Группировка этапов по группам
 *
 *        /3 \
 * 1 -> 2     5 -> 6
 *       \ 4 /
 *
 * Схлопываем в такой вид [ [1], [2], [3, 4], [5], [6] ]
 *
 * @param route
 * @param excludeDeclined
 */
export const groupSteps = (
  route: NIssue.Route,
  excludeDeclined: boolean = true
): NIssue.TransitionBoard => {
  let excludedStepsIds: Array<number> = [];
  let steps: Array<NIssue.Step>;
  if (excludeDeclined) {
    excludedStepsIds = route.steps.filter((step) => step.isDecline).map((item) => item.id);
    steps = route.steps.filter((step) => !step.isDecline);
  } else {
    steps = cloneDeep(route.steps);
  }

  // Очищаем переходы от Возвратов
  const transitions = clearFromFallbackTransitions(route.transitions);
  const startStep = findStartStep(steps);
  // Делаем мэппинг переходов
  const transitionMapping = collectTransitionMapping(transitions, startStep.id, excludedStepsIds);
  // Топологическая сортировка переходов
  const sortedTransitions = topologicalSortTransitions(transitionMapping);
  // Сортируем полученный мэппинг по полученной сортировке
  const sortedTransitionsMapping = sortTransitionMapping(transitionMapping, sortedTransitions);
  // Группируем этапы
  const groupedSteps = groupTransitionMappingToSteps(sortedTransitionsMapping, steps);

  return groupedSteps;
};

export const groupTransitionBoardSteps = (route: NIssue.Route): NIssue.TransitionBoard => {
  const groupedStepsWithoutDeclined = groupSteps(route, true);
  const groupedDeclinedSteps: Array<NIssue.Step> = route.steps.filter((s) => s.isDecline);

  // Добавляем в конец только отклоненные
  const lastIndex = Object.keys(groupedStepsWithoutDeclined).length;
  groupedStepsWithoutDeclined[lastIndex] = {
    steps: groupedDeclinedSteps,
    isParallel: false,
    routingType: null,
  };

  return groupedStepsWithoutDeclined;
};

/**
 * Ищем секцию, к которой привязано поле секции.
 * Возвращает первую из списка к которой привязано поле секции с заданным идентификатором.
 *
 * @param {Array<NIssue.Section>} sections
 * @param {number} sectionFieldId
 * @returns {NIssue.Section | null}
 */
export const findSectionByField = (
  sections: Array<NIssue.Section>,
  sectionFieldId: number
): NIssue.Section | null => {
  const section = sections.find(({ sectionFields }) =>
    sectionFields.find(({ id }) => id === sectionFieldId)
  );
  if (!section) {
    return null;
  }

  return section;
};

/**
 *
 * Функция для получения статуса ЭТАПА заявления
 * @param issue
 * @param stepChanges
 * @param routeStep
 * @returns
 */
export const getIssueStepStatus = (
  issue: NIssue.IssueCard,
  stepChanges: NIssue.StepChangeList,
  routeStep: NIssue.Step // Этап маршрута
): IssueStepStatus => {
  const currentStepId = issue.stepId;

  // Текущий этап заявления совпадает с этапом маршрута
  if (currentStepId === routeStep.id) {
    if (routeStep.isDecline) {
      return IssueStepStatus.declined;
    }
    if (routeStep.isDeclineByUser) {
      return IssueStepStatus.declinedByIssuer;
    }
    if (routeStep.isFinal) {
      return IssueStepStatus.completed;
    }
    return IssueStepStatus.inWork;
  }

  // -1 синтетический шаг
  // Проверяем, что отклонение было с предпоследнего этапа
  if (currentStepId === -1 && stepChanges.length > 1) {
    const isDeclineFromThisStep = stepChanges
      .slice(0, 2)
      .some((stepChange) => stepChange.stepId === routeStep.id);

    if (isDeclineFromThisStep) {
      return IssueStepStatus.declinedByIssuer;
    }
  }

  // Текущий этап заявления уже был пройден. FIXME: если проходил через доработку, надо это учитывать
  const endedStepIds = stepChanges
    .filter((stepChange) => stepChange.isEnded)
    .map((stepChange) => stepChange.stepId);

  if (endedStepIds.includes(routeStep.id)) {
    return IssueStepStatus.completed;
  }

  return IssueStepStatus.waiting;
};

/**
 * Функция для получения статуса ГРУППЫ ЭТАПОВ заявления
 * Определенная по ряду правил
 * @param issue
 * @param stepChanges
 * @param groupedRouteSteps
 * @returns
 */
export const getIssueGroupedStepStatus = (
  issue: NIssue.IssueCard,
  stepChanges: NIssue.StepChangeList,
  groupedRouteSteps: NIssue.TransitionBoardItem
): IssueStepStatus => {
  const groupedSteps = groupedRouteSteps.steps;

  // Получаем статус отдельно по каждому ЭТАПУ группы этапов
  const groupStepsStatuses = groupedSteps.map((step) =>
    getIssueStepStatus(issue, stepChanges, step)
  );

  console.log('GROUP STEPS', groupStepsStatuses);

  const haveInWorkStep = groupStepsStatuses.some((status) => status === IssueStepStatus.inWork);
  if (haveInWorkStep) {
    return IssueStepStatus.inWork;
  }

  // Все этапы в группе завершены
  const allCompleted = groupStepsStatuses.every((status) => status === IssueStepStatus.completed);
  if (allCompleted) {
    return IssueStepStatus.completed;
  }

  // Имеются отклоненные
  const haveDeclined = groupStepsStatuses.some((status) => status === IssueStepStatus.declined);
  if (haveDeclined) {
    return IssueStepStatus.declined;
  }

  // Имеются отклоненные заявителем
  const haveDeclinedIssuer = groupStepsStatuses.some(
    (status) => status === IssueStepStatus.declinedByIssuer
  );
  if (haveDeclinedIssuer) {
    return IssueStepStatus.declinedByIssuer;
  }

  // Хотя бы один этап в группе в режиме ожидания
  const haveWaitingStep = groupStepsStatuses.some((status) => status === IssueStepStatus.waiting);
  if (haveWaitingStep) {
    return IssueStepStatus.waiting;
  }

  return IssueStepStatus.waiting;
};

/**
 * Получить дату перехода на указанный этап
 *
 * @param step
 * @param stepChanges
 */
export const getIssueStepDate = (
  step: NIssue.Step,
  stepChanges: NIssue.StepChangeList
): string | null => {
  const correctStepChanges = stepChanges.filter((stepChange) => stepChange.stepId === step.id);
  if (correctStepChanges.length === 0) {
    return null;
  }

  const lastStepChangeWithThisStep = correctStepChanges[correctStepChanges.length - 1];

  return lastStepChangeWithThisStep.createdAt;
};

/**
 * Получить дату завершения или перехода на указанную группу
 *
 * @param groupedRouteSteps
 * @param stepChanges
 * @param type
 * @param type
 */
export const getIssueGroupStepDate = (
  groupedRouteSteps: NIssue.TransitionBoardItem,
  stepChanges: NIssue.StepChangeList,
  type: 'start' | 'end' = 'end'
): string | null => {
  const groupedSteps = groupedRouteSteps.steps;

  // Получаем время отдельно по каждому ЭТАПУ группы этапов
  const groupStepsStatusesDates = groupedSteps
    .map((step) => getIssueStepDate(step, stepChanges))
    .filter((s) => s !== null) as Array<string>;

  if (groupStepsStatusesDates.length > 0) {
    // Сортируем по дате, таким образом, что крайняя дата последняя
    groupStepsStatusesDates.sort((a, b) => b.localeCompare(a));
    if (type === 'start') {
      return groupStepsStatusesDates[0];
    }
    if (type === 'end') {
      return groupStepsStatusesDates[groupStepsStatusesDates.length - 1];
    }
  }

  return null;
};

/**
 * Вытащить значения полей из массива этапов шага
 * и положить в плоский объект
 *
 * Пример результата:
 * {1405: 123, 1406: 22}
 *
 * @copypaste
 * @param items
 * @returns
 */
export function flattenFieldValues(items: Array<NIssue.StepValues>): FieldValueSet {
  return items.reduce((acc, item) => {
    Object.assign(acc, item.fieldValues);
    return acc;
  }, {} as FieldValueSet);
}

/**
 * todo: comment
 * @param sections_
 */
export function makeSectionFieldIdSlugMapping(
  sections_: Array<NIssue.Section> | SectionsList<NotNull<Section, 'id'>>
) {
  return (sections_ as Array<NIssue.Section>).reduce<Record<string, string>>((mapping, section) => {
    section.sectionFields.forEach((sectionField) => {
      mapping[sectionField.id] = `${section.slug}.${sectionField.slug}`;
    });
    return mapping;
  }, {});
}

/**
 * todo: comment
 * @param sf
 */
export function itUsesCatalog(sf: NIssue.SectionFields) {
  return sf.field.fieldTypeId === FIELD_TYPE_SELECT.id && sf.params?.catalogSlug;
}
